import { isDepositPercentageDefault } from 'checkout/lib/utils/accommodation/deposit'
import { getCruiseBalanceDueDate } from 'checkout/lib/utils/cruises/booking'
import getPayableTotal from 'checkout/selectors/payment/getPayableTotal'
import { createSelector } from 'reselect'
import { arrayToMap, min, nonNullable } from 'lib/array/arrayUtils'
import {
  getTourV2IdForCheckoutItem,
  isAccommodationItem,
  isBNBLLEHotelItem,
  isCruiseItem,
  isInstantBookingHotelItem,
  isInstantBookingLEHotelItem,
  isLEHotelItem,
  isTourV1Item,
  isTourV2Item,
} from 'lib/checkout/checkoutUtils'
import { dateDifference, subDays } from 'lib/datetime/dateUtils'
import { OFFER_TYPE_TOUR_V2 } from 'constants/offer'
import { getStartDate } from 'lib/payment/depositsUtils'
import { getOverallConsolidatedPaymentSchedule } from 'checkout/selectors/cruiseSelectors'
import config from 'constants/config'
import { isSpoofed } from 'selectors/featuresSelectors'
import { floatify } from 'lib/maths/mathUtils'
import getDepositServiceFee from './getDepositServiceFee'
import getDepositAmountBeforeCreditApplied from './getDepositAmountBeforeCreditApplied'
import switchFunc from 'lib/function/switchFunc'
import getTourV2ItemViews from 'checkout/selectors/view/getTourV2ItemViews'
import { checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import { getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { OptimizelyExperiments } from 'constants/optimizely'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import { DEPOSIT_BALANCE_DUE_DATE_NUMBER_OF_DAYS_THRESHOLD } from 'constants/payment'
import getItemsDepositInfo from './getItemsDepositInfo'

/**
 * @remarks
 * We need this selector for the rebooking flow and cannot use getDepositAmountBeforeCreditApplied
 * because it is needed to calculate service fee. This would create a circular dependency if we
 * added service fee to it.
 */
export const getDepositAmountBeforeCreditAppliedIncludingSvcFee = createSelector(
  (state: App.State) => getDepositAmountBeforeCreditApplied(state),
  (state: App.State) => getDepositServiceFee(state),
  (depositAmount, serviceFee): number => floatify(depositAmount + serviceFee),
)

/**
 * @remarks
 * We need this selector for a few functions within this file but cannot use getAccommodationItems from /checkout/selectors/view/accommodation.ts
 * because this would create a circular import dependency. This is because accommodation.ts requires getDepositAmountPercentage from this file.
 * We have the same problem in hotels.ts, so be sure to update all 3 functions. Looking to implement a better, long term solution soon.
 */
const getAccommodationItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.AccommodationItem> => items.filter(isAccommodationItem),
  reselectShallowEqual,
)

export const getDepositDueDaysLeft = createSelector(
  (state: App.State) => getItemsDepositInfo(state),
  (itemDepositInfo): number => {
    return Math.max(...(nonNullable(Object.values(itemDepositInfo).map(deposit => deposit.dueDaysLeft))))
  },
)

export const getDepositDueDate = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => getItemsDepositInfo(state),
  (state: App.State) => getOverallConsolidatedPaymentSchedule(state),
  (state: App.State) => isSpoofed(state),
  (accommodationItems, itemsDepositInfo, consolidatedPaymentSchedule, isSpoofed): Date | undefined => {
    /**
     * The deposit/balance calculations (dates and amounts) are performed in svc-cruise,
     * however there is no reason for FE to perform new calculations. For cruises,
     * we only need to render what the API gives us. If the deposit values
     * returned by the API do not exist, we must disable the deposit
     *
     * NOTE: the selector name is misleading, as it is not deposit due date, but final payment due date
    */
    if (accommodationItems.every(item => isCruiseItem(item))) {
      return getCruiseBalanceDueDate(consolidatedPaymentSchedule)
    }

    // BNBLs have no deposit due date as dates are not selected yet
    if ((config.BNBL_DEPOSITS_ENABLED || isSpoofed) && accommodationItems.some(item => isBNBLLEHotelItem(item))) {
      // beginning of epoch as placeholder and will be used to signal BNBL deposit order
      return new Date(0)
    }

    const depositInfoMapping = arrayToMap(itemsDepositInfo, (item) => item.itemId)
    const dueDates = nonNullable(accommodationItems
      .filter(isInstantBookingHotelItem)
      .map(item => {
        const depositDueDaysLeft = depositInfoMapping.get(item.itemId)?.dueDaysLeft
        const startDate = getStartDate(item)
        return (depositDueDaysLeft && startDate) ? subDays(startDate, depositDueDaysLeft) : undefined
      }))
    return min(dueDates)
  },
)

export const isBalancePaymentDateBeyondThresholdDays = createSelector(
  (state: App.State) => getDepositDueDate(state),
  (balanceDueDate): boolean => {
    if (!balanceDueDate) return true
    const daysUntilBalanceDue = dateDifference(balanceDueDate).days
    return daysUntilBalanceDue >= DEPOSIT_BALANCE_DUE_DATE_NUMBER_OF_DAYS_THRESHOLD
  },
)

export const isDepositAllowedByOrderValue = createSelector(
  (state: App.State) => getPayableTotal(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => state.checkout.cart.currencyCode,
  (state: App.State) => state.system.depositConfigs,
  (state: App.State) => getOptimizelyExperimentVariation(state, OptimizelyExperiments.paymentsDepositOptionForHotelsAbove1999V2),
  (payableTotal, isSpoofed, currencyCode, depositConfig, depositVariation): boolean => {
    if (isSpoofed) {
      return true
    }

    if (config.DEPOSITS_LOW_ORDER_VALUE_ENABLED) {
      return true
    }

    if (!depositConfig) {
      return false
    }

    // These three brands share the same inventory and have higher than average order values so are excluded
    // for low deposit threshold which is for white labels which have lower than average order values.
    if (!['luxuryescapes', 'leagenthub', 'lebusinesstraveller'].includes(config.BRAND) &&
      payableTotal >= switchFunc<number, string>(depositConfig.WHITELABELS_DEFAULT_DEPOSIT_MINIMUMS, Infinity)(currencyCode)
    ) {
      return true
    }

    if (payableTotal >= switchFunc<number, string>(depositConfig.LUXURYESCAPES_DEFAULT_DEPOSIT_MINIMUMS, Infinity)(currencyCode)) {
      return true
    }

    if ((depositVariation) &&
      payableTotal >= switchFunc<number, string>(depositConfig.LUXURYESCAPES_ABOVE_1999_DEPOSIT_MINIMUMS, Infinity)(currencyCode)
    ) {
      return true
    }

    return false // Disallow for order values and currencies for everything else
  },
)

export const isTourV2Bundled = createSelector(
  (state: App.State) => getTourV2ItemViews(state),
  (offerViewsWithStatus): boolean => {
    return offerViewsWithStatus.data.some(offerView => offerView.isBundled)
  },
)

export const isOfferUsingDefaultDepositPercentage = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.tourV2Offers,
  (accommodationItems, offers, tourV2Offers): boolean => {
    const item = accommodationItems[0]

    if (item) {
      if (isLEHotelItem(item)) {
        if (isInstantBookingLEHotelItem(item)) {
          const offer = offers[item.offerId]
          return isDepositPercentageDefault(offer)
        }
      }

      if (isTourV1Item(item)) {
        const offer = offers[item.offerId]
        return isDepositPercentageDefault(offer)
      }

      if (isTourV2Item(item)) {
        const offer = tourV2Offers[getTourV2IdForCheckoutItem(item)]
        return offer?.depositType === 'default'
      }
    }

    return false
  },
)

export const getOfferViewWithStatusForDeposit = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (viewsWithStatus): App.WithDataStatus<App.Checkout.AccommodationOfferView> => {
    const tourV2BundleOfferView = viewsWithStatus.data.find(item => item.offerType === OFFER_TYPE_TOUR_V2 && item.isBundled)
    return {
      hasRequiredData: viewsWithStatus.hasRequiredData,
      data: tourV2BundleOfferView ?? viewsWithStatus.data[0],
    }
  },
)
